
import React, { useRef } from 'react';
import { ProductConfig, SelectedConfig, CompanyInfo } from '../types';
import { toPng } from 'html-to-image';

interface OrderSummaryProps {
  config: ProductConfig;
  selection: SelectedConfig;
  companyInfo: CompanyInfo;
  totalPrice: number;
  previewImage?: string | null;
  onClose: () => void;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ config, selection, companyInfo, totalPrice, previewImage, onClose }) => {
  const summaryRef = useRef<HTMLDivElement>(null);
  
  const size = config.sizes.find(s => s.id === selection.sizeId);
  const color = config.colors.find(c => c.id === selection.colorId);
  const headboard = config.headboards.find(h => h.id === selection.headboardId);
  const headboardVariant = headboard?.variants?.find(v => v.id === selection.headboardVariantId);
  const base = config.bases.find(b => b.id === selection.baseId);
  const poot = config.poets.find(p => p.id === selection.pootId);
  const mattress = config.mattresses.find(m => m.id === selection.mattressId);
  const topper = selection.hasTopper ? config.toppers.find(t => t.id === selection.topperId) : null;
  const nightstand = selection.nightstandId !== 'n0' ? config.nightstands.find(n => n.id === selection.nightstandId) : null;
  const voetbord = selection.hasVoetbord ? config.voetbords.find(v => v.id === selection.voetbordId) : null;
  const voet = selection.hasVoet ? config.voets.find(v => v.id === selection.voetId) : null;

  const handleDownloadImage = async () => {
    if (!summaryRef.current) return;
    
    try {
      const dataUrl = await toPng(summaryRef.current, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        style: {
          borderRadius: '0',
          boxShadow: 'none'
        },
        filter: (node) => {
          if (node instanceof HTMLStyleElement || node instanceof HTMLLinkElement) {
            try {
              const sheet = (node as any).sheet;
              if (sheet) {
                const test = sheet.cssRules;
              }
            } catch (e) {
              console.warn('Skipping restricted stylesheet during image generation', node);
              return false;
            }
          }
          return true;
        }
      });
      
      const link = document.createElement('a');
      link.download = `TheBedmaker-Order-${new Date().getTime()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to download image', err);
      alert('Er is bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const SummaryItem = ({ label, value, price }: { label: string, value?: string, price: number }) => (
    <div className="flex justify-between items-center py-4 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-sm font-bold text-gray-900">{value || 'Niet Geselecteerd'}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-gray-900">€{price.toLocaleString()}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white md:bg-gray-50 flex items-center justify-center p-0 md:p-12 font-sans">
      <div className="max-w-4xl w-full flex flex-col items-center">
        <div className="w-full px-8 py-6 bg-white md:rounded-t-[2.5rem] border-b border-gray-100 flex justify-between items-center shadow-sm z-10">
          <button onClick={onClose} className="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-[#0a0c14] transition-colors">
            ← Terug
          </button>
          <button 
            onClick={handleDownloadImage} 
            className="bg-[#0a0c14] text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg flex items-center gap-2"
          >
            <span>Opslaan 📥</span>
          </button>
        </div>

        <div 
          ref={summaryRef}
          className="w-full bg-white md:rounded-b-[2.5rem] md:shadow-2xl overflow-hidden p-8 md:p-16"
        >
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
            <div className="flex-1">
              {companyInfo.logoUrl ? <img src={companyInfo.logoUrl} alt="Logo" className="h-16 w-auto object-contain mb-6" /> : <h2 className="text-2xl font-black uppercase italic tracking-tighter text-[#0a0c14] mb-6">{companyInfo.companyName}</h2>}
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 mb-2">Besteloverzicht</h1>
              <p className="text-[11px] font-black text-[#0a0c14] uppercase tracking-[0.4em]">{config.name}</p>
            </div>
            <div className="text-left md:text-right space-y-4">
              <div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Datum</p><p className="text-sm font-bold text-gray-900">{new Date().toLocaleDateString('nl-NL')}</p></div>
              <div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Verantwoordelijke</p><p className="text-sm font-bold text-[#0a0c14] uppercase">{companyInfo.authorizedPerson}</p></div>
            </div>
          </div>

          {previewImage && (
            <div className="w-full mb-12 rounded-[2.5rem] overflow-hidden border border-gray-100 shadow-sm bg-gray-50/30 p-2">
              <img src={previewImage} alt="Bed Preview" className="w-full h-auto object-contain rounded-[2rem]" />
            </div>
          )}

          <div className="bg-zinc-50/50 rounded-[2rem] p-8 border border-zinc-100">
            <SummaryItem label="Basisprijs" value="Standaard" price={config.basePrice} />
            <SummaryItem label="Afmeting" value={size?.label} price={size?.priceModifier || 0} />
            <SummaryItem label="Kleur & Stof" value={color?.name} price={color?.priceModifier || 0} />
            <SummaryItem label="Hoofdbord Design" value={headboard?.name} price={headboard?.priceModifier || 0} />
            <SummaryItem label="Hoofdbord Maat" value={headboardVariant?.label} price={headboardVariant?.priceModifier || 0} />
            <SummaryItem label="Bodem" value={base?.name} price={base?.priceModifier || 0} />
            <SummaryItem label="Bedpootjes" value={poot?.name} price={poot?.priceModifier || 0} />
            <SummaryItem label="Matras" value={mattress?.name} price={mattress?.priceModifier || 0} />
            {topper && <SummaryItem label="Topper" value={topper.name} price={topper.priceModifier} />}
            {voetbord && <SummaryItem label="Voetbord" value={voetbord.name} price={voetbord.priceModifier} />}
            {voet && <SummaryItem label="Puff/Bankje" value={voet.name} price={voet.priceModifier} />}
            {nightstand && <SummaryItem label="Nachtkastje" value={nightstand.name} price={nightstand.priceModifier} />}

            <div className="mt-8 pt-8 border-t-2 border-zinc-200 flex justify-between items-center">
              <div><p className="text-[11px] font-black text-zinc-400 uppercase tracking-widest">Totaalbedrag (incl. BTW)</p></div>
              <div className="text-right"><p className="text-5xl font-bold tracking-tighter text-black">€{totalPrice.toLocaleString()}</p></div>
            </div>
          </div>
          
          <div className="mt-16 pt-8 border-t border-gray-100 text-[10px] text-gray-400 uppercase tracking-widest space-y-2">
            <p className="font-bold text-gray-600">{companyInfo.companyName}</p>
            <p>{companyInfo.address}</p>
            <p>{companyInfo.phone} | {companyInfo.email}</p>
            <p>{companyInfo.website}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
